import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlParams';
import { carModelAPI, BASE_URL } from '../services/api';
import './Brands.css';
import SearchableDropdown from '../components/SearchableDropdown';

function CarModels() {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [formData, setFormData] = useState({
    brandId: '',
    name: '',
    slug: '',
    image: '',
    priceRange: '',
    minPrice: '',
    maxPrice: '',
    engine: '',
    mileage: '',
    fuelType: '',
    transmissionType: '',
    seatingCapacity: '',
    avgRating: '',
    reviewCount: '',
    safetyRating: '',
    power: '',
    torque: '',
    airBags: '',
    topSpeed: '',
    bodyType: '',
    modelUrl: ''
  });
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [previewModels, setPreviewModels] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [syncingModel, setSyncingModel] = useState(null);
  const [selectedBrandForSync, setSelectedBrandForSync] = useState('');
  const [previewBrandInfo, setPreviewBrandInfo] = useState(null);

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');

  // Fetch brands for dropdown
  const fetchBrands = async () => {
    try {
      const response = await carModelAPI.getBrands();
      setBrands(response.data || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  const fetchModels = async (currentPage, currentLimit, currentSearch, currentBrandId) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: currentLimit
      };
      if (currentSearch) params.search = currentSearch;
      if (currentBrandId) params.brandId = currentBrandId;

      const response = await carModelAPI.getAll(params);

      if (Array.isArray(response.data)) {
        setModels(response.data);
        setTotal(response.data.length);
        setTotalPages(1);
      } else {
        setModels(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
      setError('');
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch car models');
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    fetchModels(page, limit, search, brandFilter);
  }, [page, limit, search, brandFilter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'name') {
      const slug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      setFormData((prev) => ({ ...prev, name: value, slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingModel) {
        await carModelAPI.update(editingModel.id, formData);
      } else {
        await carModelAPI.create(formData);
      }

      resetForm();
      fetchModels(page, limit, search, brandFilter);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save car model');
    }
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    setFormData({
      brandId: model.brandId,
      name: model.name,
      slug: model.slug,
      image: model.image || '',
      priceRange: model.priceRange || '',
      minPrice: model.minPrice || '',
      maxPrice: model.maxPrice || '',
      engine: model.engine || '',
      mileage: model.mileage || '',
      fuelType: model.fuelType || '',
      transmissionType: model.transmissionType || '',
      seatingCapacity: model.seatingCapacity || '',
      avgRating: model.avgRating || '',
      reviewCount: model.reviewCount || '',
      safetyRating: model.safetyRating || '',
      power: model.power || '',
      torque: model.torque || '',
      airBags: model.airBags || '',
      topSpeed: model.topSpeed || '',
      bodyType: model.bodyType || '',
      modelUrl: model.modelUrl || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this car model?')) {
      try {
        await carModelAPI.delete(id);
        fetchModels(page, limit, search, brandFilter);
      } catch (err) {
        setError('Failed to delete car model');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      brandId: '',
      name: '',
      slug: '',
      image: '',
      priceRange: '',
      minPrice: '',
      maxPrice: '',
      engine: '',
      mileage: '',
      fuelType: '',
      transmissionType: '',
      seatingCapacity: '',
      avgRating: '',
      reviewCount: '',
      safetyRating: '',
      power: '',
      torque: '',
      airBags: '',
      topSpeed: '',
      bodyType: '',
      modelUrl: ''
    });
    setEditingModel(null);
    setShowForm(false);
  };

  const handleSync = () => {
    setShowSyncModal(true);
    setSyncError('');
    setPreviewModels([]);
    setSelectedBrandForSync('');
    setPreviewBrandInfo(null);
  };

  const handlePreviewModels = async () => {
    if (!selectedBrandForSync) {
      setSyncError('Please select a brand first');
      return;
    }

    setLoadingPreview(true);
    setSyncError('');
    setPreviewModels([]);

    try {
      const response = await carModelAPI.preview({ brandSlug: selectedBrandForSync });
      setPreviewModels(response.data.models || []);
      setPreviewBrandInfo(response.data.brand || null);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to fetch models from CardDekho');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSyncSingle = async (model) => {
    setSyncingModel(model.slug);
    try {
      await carModelAPI.syncSingle({
        modelId: model.modelId,
        brandId: model.brandId,
        name: model.name,
        slug: model.slug,
        image: model.image,
        priceRange: model.priceRange,
        engine: model.engine,
        mileage: model.mileage,
        fuelType: model.fuelType,
        transmissionType: model.transmissionType,
        seatingCapacity: model.seatingCapacity,
        avgRating: model.avgRating,
        reviewCount: model.reviewCount,
        safetyRating: model.safetyRating,
        power: model.power,
        airBags: model.airBags,
        modelUrl: model.modelUrl
      });
      // Update the model in preview list to show as existing
      setPreviewModels(prev => prev.map(m =>
        m.slug === model.slug ? { ...m, existsInDb: true } : m
      ));
      // Refresh the main models list
      fetchModels(page, limit, search, brandFilter);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to sync model');
    } finally {
      setSyncingModel(null);
    }
  };

  const handleSyncAll = async () => {
    if (!selectedBrandForSync) {
      setSyncError('Please select a brand first');
      return;
    }

    setSyncing(true);
    try {
      await carModelAPI.sync({ brandSlug: selectedBrandForSync });
      // Refresh preview to show all as existing
      const response = await carModelAPI.preview({ brandSlug: selectedBrandForSync });
      setPreviewModels(response.data.models || []);
      setPage(1);
      fetchModels(1, limit, search, brandFilter);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to sync all models');
    } finally {
      setSyncing(false);
    }
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
    setPreviewModels([]);
    setSyncError('');
    setSelectedBrandForSync('');
    setPreviewBrandInfo(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleBrandFilterChange = (value) => {
    setBrandFilter(value);
    setPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Sync Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sync Car Models</h2>
              <button className="modal-close" onClick={closeSyncModal}>&times;</button>
            </div>
            <div className="modal-body sync-modal-body">
              <div className="sync-brand-selector">
                <label>Select Brand to Sync:</label>
                <div className="sync-brand-row">
                  <select
                    value={selectedBrandForSync}
                    onChange={(e) => setSelectedBrandForSync(e.target.value)}
                    className="brand-select"
                  >
                    <option value="">-- Select Brand --</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.slug}>{brand.brandName}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary"
                    onClick={handlePreviewModels}
                    disabled={!selectedBrandForSync || loadingPreview}
                  >
                    {loadingPreview ? 'Loading...' : 'Load Models'}
                  </button>
                </div>
              </div>

              {previewBrandInfo && (
                <div className="preview-brand-header">
                  <h3>Models for {previewBrandInfo.name}</h3>
                  <span className="model-count">{previewModels.length} models found</span>
                </div>
              )}

              {loadingPreview && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p>Loading models from CardDekho...</p>
                </div>
              )}
              {syncError && (
                <div className="sync-error">
                  <div className="error-icon">&#10005;</div>
                  <h3>Error</h3>
                  <p>{syncError}</p>
                </div>
              )}
              {!loadingPreview && !syncError && previewModels.length > 0 && (
                <div className="preview-brands-grid preview-models-grid">
                  {previewModels.map((model) => (
                    <div key={model.slug} className="preview-brand-card preview-model-card">
                      <div className="preview-brand-image preview-model-image">
                        {model.image ? (
                          <img
                            src={model.image.startsWith('http') ? model.image : `https://www.cardekho.com${model.image}`}
                            alt={model.name}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="preview-logo-placeholder">
                            {model.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="preview-brand-info preview-model-info">
                        <h4>{model.name}</h4>
                        {model.priceRange && (
                          <span className="model-price">{model.priceRange}</span>
                        )}
                        <div className="model-specs">
                          {model.fuelType && <span className="spec">{model.fuelType}</span>}
                          {model.transmissionType && <span className="spec">{model.transmissionType}</span>}
                        </div>
                      </div>
                      <button
                        className={`btn btn-sm ${model.existsInDb ? 'btn-update' : 'btn-add'}`}
                        onClick={() => handleSyncSingle(model)}
                        disabled={syncingModel === model.slug}
                      >
                        {syncingModel === model.slug ? 'Syncing...' : (model.existsInDb ? 'Update' : 'Add')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeSyncModal}>
                Close
              </button>
              <button
                className="btn btn-sync"
                onClick={handleSyncAll}
                disabled={syncing || loadingPreview || !selectedBrandForSync || previewModels.length === 0}
              >
                {syncing ? 'Syncing All...' : 'Sync All Models'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Car Models</h1>
        <div className="header-actions">
          <button
            className="btn btn-sync"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Data'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ Add New Model'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {showForm && (
        <div className="card form-card">
          <h2>{editingModel ? 'Edit Model' : 'Add New Model'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Brand *</label>
                <select
                  name="brandId"
                  value={formData.brandId}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Brand --</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.brandName}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Model Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter model name"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Slug *</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="auto-generated-slug"
                  required
                />
              </div>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="text"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="/images/model.jpg"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Price Range</label>
                <input
                  type="text"
                  name="priceRange"
                  value={formData.priceRange}
                  onChange={handleChange}
                  placeholder="Rs. 5 - 10 Lakh"
                />
              </div>
              <div className="form-group">
                <label>Engine</label>
                <input
                  type="text"
                  name="engine"
                  value={formData.engine}
                  onChange={handleChange}
                  placeholder="1197 cc"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Min Price</label>
                <input
                  type="text"
                  name="minPrice"
                  value={formData.minPrice}
                  onChange={handleChange}
                  placeholder="e.g., 5.5 Lakh"
                />
              </div>
              <div className="form-group">
                <label>Max Price</label>
                <input
                  type="text"
                  name="maxPrice"
                  value={formData.maxPrice}
                  onChange={handleChange}
                  placeholder="e.g., 10 Lakh"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Mileage</label>
                <input
                  type="text"
                  name="mileage"
                  value={formData.mileage}
                  onChange={handleChange}
                  placeholder="20 kmpl"
                />
              </div>
              <div className="form-group">
                <label>Fuel Type</label>
                <select
                  name="fuelType"
                  value={formData.fuelType}
                  onChange={handleChange}
                >
                  <option value="">Select Fuel Type</option>
                  <option value="Petrol">Petrol</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Electric">Electric</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="CNG">CNG</option>
                  <option value="Petrol/Diesel">Petrol/Diesel</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Transmission</label>
                <select
                  name="transmissionType"
                  value={formData.transmissionType}
                  onChange={handleChange}
                >
                  <option value="">Select Transmission</option>
                  <option value="Manual">Manual</option>
                  <option value="Automatic">Automatic</option>
                  <option value="AMT">AMT</option>
                  <option value="CVT">CVT</option>
                  <option value="DCT">DCT</option>
                  <option value="Manual/Automatic">Manual/Automatic</option>
                </select>
              </div>
              <div className="form-group">
                <label>Seating Capacity</label>
                <input
                  type="text"
                  name="seatingCapacity"
                  value={formData.seatingCapacity}
                  onChange={handleChange}
                  placeholder="5 Seater"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Power</label>
                <input
                  type="text"
                  name="power"
                  value={formData.power}
                  onChange={handleChange}
                  placeholder="e.g., 88.5 bhp"
                />
              </div>
              <div className="form-group">
                <label>Torque</label>
                <input
                  type="text"
                  name="torque"
                  value={formData.torque}
                  onChange={handleChange}
                  placeholder="e.g., 113 Nm"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Top Speed</label>
                <input
                  type="text"
                  name="topSpeed"
                  value={formData.topSpeed}
                  onChange={handleChange}
                  placeholder="e.g., 180 kmph"
                />
              </div>
              <div className="form-group">
                <label>Air Bags</label>
                <input
                  type="text"
                  name="airBags"
                  value={formData.airBags}
                  onChange={handleChange}
                  placeholder="e.g., 6"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Average Rating</label>
                <input
                  type="number"
                  name="avgRating"
                  value={formData.avgRating}
                  onChange={handleChange}
                  placeholder="e.g., 4.5"
                  step="0.1"
                  min="0"
                  max="5"
                />
              </div>
              <div className="form-group">
                <label>Review Count</label>
                <input
                  type="number"
                  name="reviewCount"
                  value={formData.reviewCount}
                  onChange={handleChange}
                  placeholder="e.g., 150"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Safety Rating</label>
                <input
                  type="text"
                  name="safetyRating"
                  value={formData.safetyRating}
                  onChange={handleChange}
                  placeholder="e.g., 5 Star"
                />
              </div>
              <div className="form-group">
                <label>Body Type</label>
                <select
                  name="bodyType"
                  value={formData.bodyType}
                  onChange={handleChange}
                >
                  <option value="">Select Body Type</option>
                  <option value="Hatchback">Hatchback</option>
                  <option value="Sedan">Sedan</option>
                  <option value="SUV">SUV</option>
                  <option value="MUV">MUV</option>
                  <option value="Coupe">Coupe</option>
                  <option value="Convertible">Convertible</option>
                  <option value="Pickup Truck">Pickup Truck</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Model URL</label>
                <input
                  type="text"
                  name="modelUrl"
                  value={formData.modelUrl}
                  onChange={handleChange}
                  placeholder="/maruti-suzuki/swift"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingModel ? 'Update Model' : 'Create Model'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input
              type="text"
              placeholder="Search by model name..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <SearchableDropdown
              options={brands.map(brand => ({ value: String(brand.id), label: brand.brandName }))}
              value={String(brandFilter || '')}
              onChange={handleBrandFilterChange}
              placeholder="Search brands..."
              allLabel="All Brands"
            />
          </div>
          <div className="table-options">
            <label>
              Show
              <select value={limit} onChange={handleLimitChange}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              entries
            </label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : models.length === 0 ? (
          <div className="empty-state">
            <p>No car models found. {search || brandFilter ? 'Try adjusting your filters.' : 'Add your first model or sync from CardDekho!'}</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Image</th>
                  <th>Brand</th>
                  <th>Model Name</th>
                  <th>Price Range</th>
                  <th>Fuel Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((model) => (
                  <tr key={model.id}>
                    <td>{model.id}</td>
                    <td>
                      <div className="brand-logo model-image">
                        {model.image ? (
                          <img
                            src={model.image.startsWith('/') ? `${BASE_URL}${model.image}` : model.image}
                            alt={model.name}
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div className="logo-placeholder" style={{ display: model.image ? 'none' : 'flex' }}>
                          {model.name.charAt(0)}
                        </div>
                      </div>
                    </td>
                    <td>{model.brand?.brandName || '-'}</td>
                    <td>{model.name}</td>
                    <td>{model.priceRange || '-'}</td>
                    <td>{model.fuelType || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-view"
                          onClick={() => navigate(`/car-models/${model.id}`)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-edit"
                          onClick={() => handleEdit(model)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(model.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>
    </div>
  );
}

export default CarModels;
